'use client';

import { useMemo } from 'react';
import { selectLayout, isPrimaryCandidate } from '../lib/selectLayout';
import { effectiveScore } from '../lib/recency';

/**
 * Derives tapestry composition state from scored meeting segments.
 *
 * Pure, deterministic reducer over the meeting's first `currentSent + 1`
 * sentences — scrubbing backward produces the same state as forward traversal
 * to that position. The hook itself just memoizes the replay.
 *
 * The playhead is a SENTENCE — the contract's delivery atom (TT-955 flat
 * stream) — not a display turn. `currentSent` is the 0-based ordinal of the
 * last sentence to replay in `segments` flattened in order (-1 renders
 * nothing); recency and hysteresis run in those same units.
 *
 * It must be a sentence and not a turn (TT-971): turns are runs of the same
 * speaker (streamSegments), so a live meeting grows INSIDE its last turn — at
 * per-utterance cadence a single-speaker meeting is one giant turn that never
 * stops growing. A turn index therefore cannot name a position in a live
 * meeting at all, which is why pausing on a turn index didn't pause anything.
 * Turn-stepping hosts convert with `turnEndSentence` (lib/streamSegments).
 *
 * Output state:
 *   - symbols: Map<symbol_id, SymbolState> with { count, firstIdx, lastIdx, role, sentences }
 *     (firstIdx/lastIdx are stream sentence indexes; only ever compared to
 *     each other / used as ordering + seeds)
 *   - playheadSent: stream index of the last sentence replayed — the contract's
 *     own `sent.idx` where the payload carries one, so it is NOT interchangeable
 *     with the `currentSent` ordinal the caller passes in
 *   - primary: symbol_id | null
 *   - ranked: symbol_ids sorted by count desc (tiebreak: earlier firstIdx)
 *   - layout: { backgroundId, layoutId, style, confirmed } | null  (curated pick)
 *   - events: list of events emitted AT the final sentence step (animation cues)
 *
 * Role assignment (post-hysteresis):
 *   primary (1) | secondary (up to MAX_SECONDARY) | tertiary (up to MAX_TERTIARY)
 *
 * Only symbols in the curated primary-symbol whitelist (carried on the catalog
 * as `primarySymbolIds`, sourced from appback /tap/symbols/primary) are eligible for
 * the primary role (and thus to drive background selection); every other symbol
 * stays a secondary/tertiary foreground symbol regardless of count. This keeps
 * the center + backdrop on a curated symbol that actually has artwork. When the
 * whitelist is absent/empty, no gating is applied.
 *
 * Primary hysteresis: a challenger must out-count the reigning primary by
 * PRIMARY_MARGIN, sustained for PRIMARY_PERSIST consecutive sentences, before
 * taking over. Prevents thrashing on single noisy sentences.
 *
 * Layout hysteresis: when a `catalog` is supplied, selectLayout runs each
 * sentence; a candidate background must win LAYOUT_PERSIST consecutive sentences
 * (and only once the leading symbol has real mass) before it is committed.
 * Mirrors the primary gate so the curated layout doesn't flip before it's a
 * solid choice. Until committed, `layout` is null and the host keeps rendering
 * the generative packer.
 *
 * Events emitted at the current step:
 *   { type: "symbol.introduce", symbolId, count }
 *   { type: "symbol.reinforce", symbolId, count, delta }
 *   { type: "sentence.surface", text, score, symbolIds }
 *   { type: "primary.change",   from, to }
 *   { type: "layout.change",    from, to, style, symbolId }
 */

const PRIMARY_MARGIN = 1.2;
// Persistence gates are in SENTENCE steps. The old values (2) were in display
// segments — a segment averaged a handful of sentences, so these preserve the
// same content-scale of hysteresis at the finer clock.
const PRIMARY_PERSIST = 8;
const MAX_SECONDARY = 3;
const MAX_TERTIARY = 3;

// A candidate background must be the selection winner for this many consecutive
// sentences before we commit to it (also gates switching to a new background).
// Kept low so a background is chosen promptly and switches stay responsive when
// the meeting's topic shifts — just enough to filter out single-sentence noise.
const LAYOUT_PERSIST = 8;

// The leading symbol must reach this much accumulated mass before we start
// evaluating a background — enough to avoid committing off one noisy early
// segment, but low enough that a background commits early.
const LAYOUT_MIN_COUNT = 4;

// Don't promote a symbol to the central "primary" placement until we're
// reasonably sure it's the dominant theme. Until both conditions are met,
// the leading symbol is rendered as a secondary so the layout fills the
// edges first and leaves the center empty rather than committing to a
// large central symbol that may flip moments later.
const PRIMARY_MIN_COUNT = 3;
const PRIMARY_MIN_LEAD = 1;
// Retain a generous buffer per symbol so the curated layout has enough distinct
// sentences/clauses to fill all of a layout's textpaths (fillSlots).
const MAX_SENT_PER_SYMBOL = 16;
const DEFAULT_THRESHOLD = 0.5;

// Recency weighting lives in lib/recency.js — a sentence's effective weight is
// score × a decay in SENTENCES, so fresh sentences out-compete stale
// high-scorers for buffer space (eviction below) and for display slots. Curated
// slot filling shares that math and layers its own per-slot temperaments on top
// to balance fresh-vs-anchored text across a scene (TIERS in fillSlots — TT-973).

function normalizeSentence(s) {
  if (typeof s === 'string') return { text: s, display_score: 0, symbol_ids: [] };
  return s;
}

/**
 * Pure reducer: replays the first `currentSent + 1` sentences of `segments`
 * and returns composition state. Also returns the events that were emitted on
 * the final step so a caller can drive animations off them without retaining
 * its own history.
 *
 * Exported (alongside the hook) as the testable pure core.
 */
export function reduceTapestry(segments, currentSent, threshold = DEFAULT_THRESHOLD, catalog = null, seed = null) {
  const symbols = new Map(); // symbol_id -> { count, firstIdx, lastIdx, sentences }
  let primary = null;
  let challenger = null;
  let challengerStreak = 0;
  let events = [];

  // Layout selection hysteresis (only when a catalog is supplied).
  let committedLayout = null; // { backgroundId, layoutId, style }
  let layoutChallenger = null; // { backgroundId, layoutId, style }
  let layoutStreak = 0;

  // Curated whitelist of symbols eligible for the primary role + background
  // selection (carried on the catalog). Empty/absent ⇒ no gating.
  const primarySet = catalog?.primarySymbolIds;

  // Flatten the segments into per-sentence steps, cutting at the playhead.
  // Each step's `sidx` prefers the contract's own `idx` (the flat-stream
  // sentence index); checkpoint/legacy payloads without one fall back to the
  // ordinal, which is deterministic because replay always starts at 0.
  const limit = Math.max(0, (currentSent ?? -1) + 1);
  const steps = [];
  for (const seg of segments || []) {
    if (steps.length >= limit) break;
    for (const rawS of seg?.sentences || []) {
      if (steps.length >= limit) break;
      steps.push({ s: normalizeSentence(rawS), sidx: rawS?.idx ?? steps.length });
    }
  }
  // The playhead as a stream index — recency ages are measured against this.
  const playheadSent = steps.length ? steps[steps.length - 1].sidx : -1;

  for (const { s, sidx } of steps) {
    // Collect what happened at this sentence
    const stepCounts = {}; // symbol_id -> delta
    const stepSentences = []; // high-scoring sentences at this step
    if (s.display_score >= threshold) {
      if (s.text) {
        stepSentences.push({
          text: s.text,
          score: s.display_score,
          symbolIds: s.symbol_ids || [],
          idx: sidx, // global sentence index (for recency decay)
        });
      }
      for (const symbolId of s.symbol_ids || []) {
        stepCounts[symbolId] = (stepCounts[symbolId] || 0) + 1;
      }
    }

    // Reset per-step event log; we only surface events from the final step
    events = [];

    for (const [symbolId, delta] of Object.entries(stepCounts)) {
      const prev = symbols.get(symbolId);
      if (!prev) {
        const entry = {
          count: delta,
          firstIdx: sidx,
          lastIdx: sidx,
          sentences: [],
        };
        symbols.set(symbolId, entry);
        events.push({ type: 'symbol.introduce', symbolId, count: delta });
      } else {
        prev.count += delta;
        prev.lastIdx = sidx;
        events.push({ type: 'symbol.reinforce', symbolId, count: prev.count, delta });
      }
    }

    // Attach sentences to their symbol state. We keep a buffer of the
    // highest-scoring sentences seen so far for each symbol, not the
    // latest — display_score is the enriched segment salience metric and we
    // want the best of the meeting's history, not just what's fresh.
    for (const sent of stepSentences) {
      events.push({
        type: 'sentence.surface',
        text: sent.text,
        score: sent.score,
        symbolIds: sent.symbolIds,
      });
      for (const symbolId of sent.symbolIds) {
        const entry = symbols.get(symbolId);
        if (!entry) continue;
        // Skip duplicates (same text already in buffer)
        if (entry.sentences.some((prior) => prior.text === sent.text)) continue;
        entry.sentences.push(sent);
        if (entry.sentences.length > MAX_SENT_PER_SYMBOL) {
          // Evict the lowest effective (recency-weighted) score, so stale
          // sentences age out and make room for fresh ones.
          let minIdx = 0;
          let minScore = effectiveScore(entry.sentences[0], sidx);
          for (let k = 1; k < entry.sentences.length; k++) {
            const sc = effectiveScore(entry.sentences[k], sidx);
            if (sc < minScore) {
              minScore = sc;
              minIdx = k;
            }
          }
          entry.sentences.splice(minIdx, 1);
        }
      }
    }

    // Find the current highest-count whitelisted symbol for hysteresis. Only
    // PRIMARY_SYMBOLS may hold the primary role (and drive background
    // selection); other symbols stay secondary/tertiary foreground. Single-pass
    // max with firstIdx tiebreak — avoids allocating + sorting the whole
    // entries array once per step.
    let topId = null;
    let topCount = -1;
    let topFirstIdx = Infinity;
    for (const [symbolId, entry] of symbols) {
      if (!isPrimaryCandidate(symbolId, primarySet)) continue;
      if (entry.count > topCount || (entry.count === topCount && entry.firstIdx < topFirstIdx)) {
        topCount = entry.count;
        topFirstIdx = entry.firstIdx;
        topId = symbolId;
      }
    }

    if (!primary) {
      if (topId) {
        primary = topId;
        events.push({ type: 'primary.change', from: null, to: topId });
      }
      challenger = null;
      challengerStreak = 0;
    } else if (topId && topId !== primary) {
      const topCount = symbols.get(topId).count;
      const primaryCount = symbols.get(primary).count;
      if (topCount > primaryCount * PRIMARY_MARGIN) {
        if (challenger === topId) {
          challengerStreak++;
        } else {
          challenger = topId;
          challengerStreak = 1;
        }
        if (challengerStreak >= PRIMARY_PERSIST) {
          events.push({ type: 'primary.change', from: primary, to: topId });
          primary = topId;
          challenger = null;
          challengerStreak = 0;
        }
      } else {
        challenger = null;
        challengerStreak = 0;
      }
    } else {
      challenger = null;
      challengerStreak = 0;
    }

    // ---- Layout selection hysteresis ----
    // Only evaluate once the leading symbol has real mass, so a background isn't
    // committed off a few noisy early sentences. A challenger must win
    // LAYOUT_PERSIST consecutive sentences to take over (or to first commit).
    if (catalog && topCount >= LAYOUT_MIN_COUNT) {
      const counts = new Map();
      for (const [symbolId, entry] of symbols) counts.set(symbolId, entry.count);
      const pick = selectLayout(counts, catalog, seed);
      if (pick) {
        if (committedLayout && pick.backgroundId === committedLayout.backgroundId) {
          layoutChallenger = null;
          layoutStreak = 0;
        } else if (layoutChallenger && pick.backgroundId === layoutChallenger.backgroundId) {
          layoutStreak++;
          if (layoutStreak >= LAYOUT_PERSIST) {
            events.push({
              type: 'layout.change',
              from: committedLayout ? committedLayout.backgroundId : null,
              to: pick.backgroundId,
              style: pick.style,
              symbolId: pick.matchedSymbolId,
            });
            committedLayout = pick;
            layoutChallenger = null;
            layoutStreak = 0;
          }
        } else {
          layoutChallenger = pick;
          layoutStreak = 1;
        }
      }
    }
  }

  // Final ranking and role assignment
  const ranked = [...symbols.entries()].sort((a, b) => {
    if (b[1].count !== a[1].count) return b[1].count - a[1].count;
    return a[1].firstIdx - b[1].firstIdx;
  });

  // Confirm the primary only once it has enough mass and a clear lead
  // over the runner-up. Internal hysteresis (PRIMARY_MARGIN/PRIMARY_PERSIST
  // above) handles thrashing once a primary is committed; this gate
  // prevents committing too early.
  const primaryEntry = primary ? symbols.get(primary) : null;
  // Compare against the next whitelisted contender, not the overall runner-up:
  // a high-count non-candidate (e.g. people circle) isn't competing for the
  // center, so it must not gate the primary's confirmation.
  const runnerUp = ranked.find(([symbolId]) => symbolId !== primary && isPrimaryCandidate(symbolId, primarySet));
  const runnerUpCount = runnerUp ? runnerUp[1].count : 0;
  const primaryConfirmed =
    !!primaryEntry && primaryEntry.count >= PRIMARY_MIN_COUNT && primaryEntry.count - runnerUpCount >= PRIMARY_MIN_LEAD;

  const roles = new Map();
  let secondaryLeft = MAX_SECONDARY;
  let tertiaryLeft = MAX_TERTIARY;
  for (const [symbolId] of ranked) {
    if (symbolId === primary && primaryConfirmed) {
      roles.set(symbolId, 'primary');
    } else if (secondaryLeft > 0) {
      roles.set(symbolId, 'secondary');
      secondaryLeft--;
    } else if (tertiaryLeft > 0) {
      roles.set(symbolId, 'tertiary');
      tertiaryLeft--;
    } else {
      roles.set(symbolId, 'hidden');
    }
  }

  // Attach roles to symbol entries for downstream convenience, and stamp each
  // sentence's recency-weighted score relative to the sentence playhead so the
  // packer / fillSlots can rank by it without knowing the stream index.
  const symbolMap = {};
  for (const [symbolId, entry] of symbols.entries()) {
    for (const s of entry.sentences) s.effectiveScore = effectiveScore(s, playheadSent);
    symbolMap[symbolId] = { ...entry, role: roles.get(symbolId) || 'hidden' };
  }

  return {
    symbols: symbolMap,
    primary,
    ranked: ranked.map(([symbolId]) => symbolId),
    // The reducer's clock: the stream index of the last sentence replayed (-1
    // before any). Downstream content policy measures age and dwell in these
    // units (fillSlots — TT-973).
    playheadSent,
    layout: committedLayout
      ? {
          backgroundId: committedLayout.backgroundId,
          layoutId: committedLayout.layoutId,
          style: committedLayout.style,
          matchedSymbolId: committedLayout.matchedSymbolId,
          primaryMatch: committedLayout.primaryMatch,
          confirmed: true,
        }
      : null,
    events,
  };
}

/**
 * React hook wrapper — memoizes the reducer on (segments, currentSent, threshold,
 * catalog). `catalog` (from prepareLayoutCatalog) is optional; without it the
 * reducer skips layout selection and `state.layout` stays null.
 */
export default function useTapestryState(
  segments,
  currentSent,
  threshold = DEFAULT_THRESHOLD,
  catalog = null,
  seed = null
) {
  return useMemo(
    () => reduceTapestry(segments || [], currentSent ?? -1, threshold, catalog, seed),
    [segments, currentSent, threshold, catalog, seed]
  );
}
